import React, { useRef, useState } from 'react';
import { validateClaimUpload } from '../data/claimDocumentUpload';

// SECTION 412 PART B - staging exhibit FILES at the exhibits step of the claim flow, so a member
// attaches his evidence where the claim asks for it rather than filing the claim and reopening it to
// upload. Controlled: the parent holds the array [{ file, description }] and persists nothing until the
// claim exists (the files upload on submit through the claimDocumentUploadUrl callable). The same
// validation the callable runs is run here on selection, so a bad file is refused at the field.
//
// Same accept list as ClaimDocuments.js. Nothing here writes storage; it only stages File objects.
const ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xls,.xlsx';

function fmtSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function ExhibitFileAttach({ files = [], onChange, disabled }) {
  const inputRef = useRef();
  const [error, setError] = useState('');

  const addFiles = (fileList) => {
    setError('');
    const incoming = Array.from(fileList || []);
    const accepted = [];
    const rejected = [];
    for (const f of incoming) {
      const v = validateClaimUpload({ contentType: f.type, bytes: f.size });
      if (v.ok) accepted.push({ file: f, description: f.name });
      else rejected.push(f.name + ' - ' + v.why);
    }
    if (rejected.length) setError(rejected.join('  |  '));
    if (accepted.length) onChange([...(files || []), ...accepted]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const setDescription = (i, desc) => onChange(files.map((it, j) => (j === i ? { ...it, description: desc } : it)));
  const remove = (i) => onChange(files.filter((_, j) => j !== i));

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>
        Attach exhibit files
      </div>
      <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 8 }}>
        Attach the actual documents here — they upload securely when you file the claim, and appear under
        the claim's Documents &amp; Exhibits. PDF, Word, Excel, images or text, up to 25&nbsp;MB each.
      </div>

      {error && (
        <div style={{ background: '#2D0A0A', border: '1px solid #C0392B', borderRadius: 6, padding: '8px 12px', marginBottom: 8, color: '#FF6B6B', fontSize: 11 }}>
          {error}
        </div>
      )}

      <input aria-label="Attach an exhibit"
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        disabled={disabled}
        onChange={(e) => addFiles(e.target.files)}
        style={{ display: 'none' }}
        id="exhibit-file-attach"
      />
      <label
        htmlFor="exhibit-file-attach"
        style={{ padding: '7px 14px', background: disabled ? '#1A2E44' : '#1A3A5C', border: '1px solid #2E4A6B',
                 borderRadius: 6, fontSize: 12, color: disabled ? '#4A6280' : '#F4F6F8',
                 cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-block' }}>
        📎 Select exhibit files
      </label>

      {files.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {files.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', background: '#0A1628',
                                  border: '1px solid #2E4A6B', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
              <span style={{ fontSize: 16 }}>📄</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#F4F6F8', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.file.name} <span style={{ color: '#4A6280', fontWeight: 400 }}>· {fmtSize(it.file.size)}</span>
                </div>
                <input aria-label="Describe this exhibit (optional)"
                  style={{ marginTop: 4, width: '100%', fontSize: 11, background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 4, padding: '4px 8px', color: '#F4F6F8' }}
                  placeholder="Describe this exhibit (optional)"
                  value={it.description || ''}
                  onChange={(e) => setDescription(i, e.target.value)}
                  disabled={disabled} />
              </div>
              <button type="button" onClick={() => remove(i)} disabled={disabled}
                title="Remove this file"
                style={{ background: 'none', border: 'none', color: '#C0392B', fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer', lineHeight: 1 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
